import React from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, CheckCircle2, CalendarCheck, Users, Clock,
  ArrowUpRight, Briefcase, Target, RefreshCw,
} from "lucide-react";
import { useApiData } from "../../lib/api.js";
import GradientHeader from "../../components/GradientHeader.jsx";
import { Stranka, Dlazdice, EmptyState, Hlaska, AkceZnovu, KartaSeznam, SkeletonRows, Stitek } from "../../components/ui.jsx";

// === OPERATIONS - CEO přehled ===
// Kdo má kolik otevřených úkolů, co je po termínu, kdo a kdy trackoval čas.
//
// 4.9.2026 (fáze 4a): stránka se překreslila do vzorníku. Data, endpoint ani
// odkazy se nemění, mění se jen podoba: gradientová hlavička, dlaždice s
// barevným proužkem, karty se seznamem místo tabulek (na telefonu se tabulky
// nevešly a přetékaly), pill boxy místo tečkových oddělovačů.

const TYP_LABEL = { tydenni: "Týdenní", mesicni: "Měsíční", kvartalni: "Kvartální", rocni: "Roční" };
const TYP_TON = { tydenni: "znacka", mesicni: "info", kvartalni: "pozor", rocni: "ok" };

function fmtCz(iso) {
  if (!iso) return "—";
  const p = String(iso).slice(0, 10).split("-");
  return p.length === 3 ? `${Number(p[2])}. ${Number(p[1])}.` : iso;
}

/** Řádek člověka s čísly úkolů. Dřív to byla tabulka o čtyřech sloupcích. */
function RadekOsoby({ r }) {
  return (
    <div className="flex min-h-[64px] items-center gap-3 px-4 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-slate-800 demo-sensitive">{r.pilot}</div>
        <div className="mt-1 flex flex-wrap gap-1.5">
          <Stitek velikost="mala" ton="neutralni">{r.open} otevřených</Stitek>
          {r.overdue > 0 && <Stitek velikost="mala" ton="problem">{r.overdue} po termínu</Stitek>}
          {r.done30 > 0 && <Stitek velikost="mala" ton="ok">{r.done30} hotovo za 30 dní</Stitek>}
        </div>
      </div>
    </div>
  );
}

function KartaOsob({ title, ikona, rows, linkTo, loading }) {
  return (
    <KartaSeznam
      ikona={ikona}
      title={title}
      odznak={rows?.length || undefined}
      akce={linkTo && (
        <Link to={linkTo} aria-label="Otevřít" title="Otevřít"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white sm:h-9 sm:w-9">
          <ArrowUpRight className="h-4 w-4" />
        </Link>
      )}
    >
      {loading && !rows ? (
        <SkeletonRows rows={4} />
      ) : !rows || rows.length === 0 ? (
        <EmptyState icon={CheckCircle2} title="Žádné úkoly" hint="Tenhle tým nemá nic otevřeného." />
      ) : (
        <div className="divide-y divide-slate-100">
          {rows.map((r) => <RadekOsoby key={r.pilot} r={r} />)}
        </div>
      )}
    </KartaSeznam>
  );
}

function KartaPoTerminu({ title, rows, linkTo, loading }) {
  return (
    <KartaSeznam
      ikona={AlertTriangle}
      title={title}
      odznak={rows?.length || 0}
      odznakTon={rows?.length ? "zluta" : "bila"}
    >
      {loading && !rows ? (
        <SkeletonRows rows={4} />
      ) : !rows || rows.length === 0 ? (
        <EmptyState icon={CheckCircle2} title="Nic po termínu" hint="Všechno je stihnuté včas." />
      ) : (
        <div className="max-h-96 divide-y divide-slate-100 overflow-y-auto overscroll-contain">
          {rows.map((r) => (
            <div key={r.id} className="flex min-h-[64px] items-center gap-3 px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-slate-800">{r.nazev}</div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  <Stitek velikost="mala" ton="problem">{r.dniPo} dní po termínu</Stitek>
                  <Stitek velikost="mala" ton="neutralni">{fmtCz(r.deadline)}</Stitek>
                  {r.pilot && <Stitek velikost="mala" ton="neutralni" className="demo-sensitive">{r.pilot}</Stitek>}
                  {r.kategorie && <Stitek velikost="mala" ton="neutralni">{r.kategorie}</Stitek>}
                </div>
              </div>
              <Link to={linkTo} aria-label="Otevřít úkol" title="Otevřít úkol" className="btn-ikona shrink-0">
                <ArrowUpRight className="h-5 w-5" />
              </Link>
            </div>
          ))}
        </div>
      )}
    </KartaSeznam>
  );
}

export default function OperationsPrehled() {
  const { data, loading, error, refetch, refreshing } = useApiData("/api/operations-prehled");
  const k = data?.kpis || {};
  const time = data?.time || { days: [], rows: [] };
  const openMeetings = data?.meetings?.open || [];

  return (
    <Stranka>
      <GradientHeader
        title="Operations"
        icon={Target}
        loading={refreshing}
        akce={[{ klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch }]}
      />

      {error && (
        <Hlaska ton="problem" title="Přehled se nepodařilo načíst" akce={<AkceZnovu onClick={refetch} />}>
          {String(error.message || error)}
        </Hlaska>
      )}

      {/* Čísla nahoře. Barevný proužek dlaždice nese význam. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Dlazdice popisek="Otevřené, management" ton="znacka" hodnota={data ? (k.companyOpen ?? 0) : "—"}
          doplnky={data ? <Stitek velikost="mala" ton={k.companyOverdue > 0 ? "problem" : "neutralni"}>{k.companyOverdue ?? 0} po termínu</Stitek> : null} />
        <Dlazdice popisek="Otevřené, trenéři" ton="info" hodnota={data ? (k.trenerOpen ?? 0) : "—"}
          doplnky={data ? <Stitek velikost="mala" ton={k.trenerOverdue > 0 ? "problem" : "neutralni"}>{k.trenerOverdue ?? 0} po termínu</Stitek> : null} />
        <Dlazdice popisek="Hotové za 30 dní" ton="ok" hodnotaTon="ok" hodnota={data ? (k.companyDone30 ?? 0) : "—"}
          doplnky={<Stitek velikost="mala" ton="neutralni">management board</Stitek>} />
        <Dlazdice popisek="Otevřené meetingy" ton="pozor" hodnota={data ? (k.openMeetings ?? 0) : "—"}
          doplnky={openMeetings[0] ? <Stitek velikost="mala" ton="neutralni">{openMeetings[0].nazev}</Stitek> : null} />
      </div>

      {/* Úkoly per osoba */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <KartaOsob title="Úkoly týmu, management" ikona={Briefcase} rows={data?.company?.perPerson} linkTo="/operations/ukoly" loading={loading} />
        <KartaOsob title="Úkoly týmu, trenéři" ikona={Users} rows={data?.treneri?.perPerson} linkTo="/manager/treneri/ukoly" loading={loading} />
      </div>

      {/* Po termínu */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <KartaPoTerminu title="Management po termínu" rows={data?.company?.overdue} linkTo="/operations/ukoly" loading={loading} />
        <KartaPoTerminu title="Trenéři po termínu" rows={data?.treneri?.overdue} linkTo="/manager/treneri/ukoly" loading={loading} />
      </div>

      {/* Time tracking. Tabulka se na telefonu nevešla, teď je to karta na osobu. */}
      <KartaSeznam
        ikona={Clock}
        title="Time tracking, posledních 7 dní"
        podtitulek="kdo trackoval čas a odevzdal denní report"
        akce={
          <Link to="/operations/time-tracking" aria-label="Otevřít time tracking" title="Otevřít time tracking"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white sm:h-9 sm:w-9">
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        }
      >
        {loading && !time.rows?.length ? (
          <SkeletonRows rows={4} />
        ) : !time.rows || time.rows.length === 0 ? (
          <EmptyState icon={Clock} title="Nikdo netrackoval" hint="Time tracking mají role manager, CSM, sales a výživová." />
        ) : (
          <div className="divide-y divide-slate-100">
            {time.rows.map((p) => (
              <div key={p.email} className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-bold text-slate-900 demo-sensitive">{p.name}</div>
                    <div className="truncate text-[11px] text-slate-500">{p.role}</div>
                  </div>
                  <Stitek ton="neutralni">{p.totalHours} h</Stitek>
                </div>
                <div className="mt-2 grid grid-cols-7 gap-1">
                  {p.days.map((d) => (
                    <div key={d.datum}
                      title={`${fmtCz(d.datum)}: ${d.hours > 0 ? `${d.hours} h` : "nic naměřeno"}${d.filled ? ", report odevzdaný" : ""}`}
                      className={`flex min-h-[52px] flex-col rounded-xl border p-1 ${
                        d.hours > 0 ? "border-emerald-200 bg-emerald-50" : "border-slate-100 bg-slate-50"
                      }`}>
                      <div className="text-[11px] leading-none text-slate-400">{Number(String(d.datum).slice(8, 10))}</div>
                      <div className="mt-auto text-sm font-bold tabular-nums text-slate-800">{d.hours > 0 ? d.hours : "—"}</div>
                      <div className={`mt-0.5 h-1 w-full rounded-full ${d.filled ? "bg-emerald-500" : d.hours > 0 ? "bg-rose-500" : "bg-transparent"}`} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5">
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500"><span className="h-1 w-5 rounded-full bg-emerald-500" /> report odevzdaný</span>
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500"><span className="h-1 w-5 rounded-full bg-rose-500" /> report chybí</span>
              <span className="text-[11px] font-semibold text-slate-500">číslo je počet hodin</span>
            </div>
          </div>
        )}
      </KartaSeznam>

      {/* Meetingy */}
      <KartaSeznam ikona={CalendarCheck} title="Otevřené meetingy" odznak={openMeetings.length || undefined}>
        {openMeetings.length === 0 ? (
          <EmptyState
            icon={CalendarCheck}
            title="Žádný otevřený meeting"
            hint="Založ první a zapiš do něj, co se řešilo."
            action={<Link to="/operations/meetingy" className="btn btn-primary btn-sm">Otevřít meetingy</Link>}
          />
        ) : (
          <div className="divide-y divide-slate-100">
            {openMeetings.map((m) => (
              <div key={m.id} className="flex min-h-[64px] items-center gap-3 px-4 py-2.5">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-slate-800">{m.nazev}</div>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    <Stitek velikost="mala" ton={TYP_TON[m.typ] || "neutralni"}>{TYP_LABEL[m.typ] || m.typ}</Stitek>
                    <Stitek velikost="mala" ton="neutralni">{fmtCz(m.datum)}</Stitek>
                  </div>
                </div>
                <Link to={`/operations/meetingy?open=${m.id}`} aria-label="Otevřít meeting" title="Otevřít meeting" className="btn-ikona shrink-0">
                  <ArrowUpRight className="h-5 w-5" />
                </Link>
              </div>
            ))}
          </div>
        )}
      </KartaSeznam>
    </Stranka>
  );
}
